import type {
  CameraWaypoint,
  DeskLoginConfig,
  EnvironmentSpec,
  InvoiceConfig,
  MfitCloudDashConfig,
  ScenarioSpec,
  ScoringEvent,
} from "./types";
import { quishingRestaurantScenario } from "./quishing-restaurant.scenario";

/**
 * The Invoice That Changed — supplier bank-detail change / invoice fraud (BEC)
 * at the fictional Bellmont Museum of Art. The whole course runs on the
 * south-wing desk PC in the already-rendered museum-hall glTF (guided camera
 * walks in and zooms to the monitor); the wall TV is used only for the final
 * summary; the attacker room is reused from quishing-restaurant. Every id here
 * is new (prefix inv_ / wp_inv_).
 *
 * The invoice was real. The supplier was real. The bank details were not.
 * Every mail, call, bank account and payment is simulated.
 */

const HACKER_ENV = quishingRestaurantScenario.scene.environments?.find((e) => e.id === "hacker")?.environment;
if (!HACKER_ENV) throw new Error('quishing-restaurant environment "hacker" not found');

function waypoint(scenario: ScenarioSpec, id: string, newId: string, durationS?: number): CameraWaypoint {
  const value = scenario.scene.waypoints.find((w) => w.id === id);
  if (!value) throw new Error(`${scenario.id} waypoint "${id}" not found`);
  return { ...value, id: newId, ...(durationS == null ? {} : { durationS }) };
}

const ASSETS = "/cdn/sim/invoice-that-changed";
const FILM = "/cdn/sim/awareness-videos/invoice-changed/invoice-changed.mp4?v=2";

const CLAIRE = { name: "Claire Dubois", role: "Exhibition Director", avatarUrl: `${ASSETS}/img/claire.png`, voiceId: "shimmer" };
const LENA = { name: "Lena Vogt", role: "Aurelia Fine Art Transport AG", avatarUrl: `${ASSETS}/img/lena.png`, voiceId: "coral" };
const ACCOUNTS = { name: "Aurelia Accounts", role: "Caller ID shown by the revised invoice", avatarUrl: `${ASSETS}/img/accounts.png`, voiceId: "echo" };

const ALEX_MAIL = "alex.morgan@bellmontmuseum.ch";

// ---------------------------------------------------------------- environment
// The museum-hall glTF, guided camera (walkable off). The procedural key only
// scopes the desk-monitor screen-box CSS (.sim-root[data-env="museum_hall"] in
// invoice.css); the room itself is the glTF. v4 = the three screen textures
// darkened to powered-off glass (no mirrored/upside-down painted screens).
const MUSEUM_ENV: EnvironmentSpec = {
  procedural: "museum_hall" as unknown as EnvironmentSpec["procedural"],
  gltfUrl: "/cdn/sim/museum-hall/museum.glb?v=5",
  gltfScale: 1,
  gltfPosition: [0, 0, 0],
  gltfRotationDeg: [0, 0, 0],
  enableSplat: false,
  enablePano: false,
  splat: {},
  exposure: 0.58,
  backdropColor: "#cfc8ba",
  // north-wall TV glass (used by the summary camera; the DOM is glued by dead-on framing)
  screenQuad: { center: [0, 2.2, 15.518], width: 3.24, height: 1.82, rotationDeg: 180 },
};

// ---------------------------------------------------------------- scoring
const EVENTS: ScoringEvent[] = [
  { id: "inv_read_original", label: "Checked the approved invoice and supplier record", kind: "positive", weight: 5, dimension: "operations", collectEvidence: true },
  { id: "inv_detected_change", label: "Spotted the changed bank account", kind: "positive", weight: 15, dimension: "information", collectEvidence: true, detail: "The invoice number, amount and supplier all matched. Only the beneficiary account had changed." },
  { id: "inv_replied_thread", label: "Replied inside the supplier's email thread", kind: "positive", weight: 0, dimension: "operations", collectEvidence: true, detail: "A reply to the compromised mailbox is answered by the attacker. It is not independent verification." },
  { id: "inv_called_revised", label: "Called the number printed on the revised invoice", kind: "positive", weight: 0, dimension: "operations", collectEvidence: true, detail: "The number came from the altered document, so the call reaches the attacker. Not independent verification." },
  { id: "inv_called_trusted", label: "Called the supplier on the number already on file", kind: "positive", weight: 30, dimension: "operations", collectEvidence: true, detail: "An independent channel from before the request. The real supplier denied the change." },
  { id: "inv_held", label: "Held the unverified payment", kind: "positive", weight: 20, dimension: "operations", collectEvidence: true },
  { id: "inv_reported", label: "Reported the suspected invoice fraud", kind: "positive", weight: 15, dimension: "operations", collectEvidence: true },
  { id: "inv_notified_director", label: "Kept the director informed without giving in to pressure", kind: "positive", weight: 10, dimension: "operations", collectEvidence: true },
  { id: "inv_paid_original", label: "Paid the approved account under normal controls", kind: "positive", weight: 10, dimension: "operations", collectEvidence: true },
  { id: "inv_called_bank", label: "Called the bank at once to try to recall the payment", kind: "positive", weight: 10, dimension: "money", collectEvidence: true },
  { id: "inv_escalated", label: "Alerted Finance, IT and the supplier after the payment", kind: "positive", weight: 10, dimension: "operations", collectEvidence: true },
  {
    id: "inv_paid_revised",
    label: "Paid the changed bank account without independent verification",
    kind: "risk",
    weight: 60,
    dimension: "money",
    collectEvidence: true,
    detail: "CHF 48,600 was sent to the attacker's account. The invoice was genuine; only the bank details had been altered inside a trusted conversation.",
  },
];

// ---------------------------------------------------------------- fixtures shared by devices
const FIX = {
  supplier: "Aurelia Fine Art Transport AG",
  invoiceId: "AT-4021",
  po: "BM-2187",
  amount: "CHF 48,600.00",
  purpose: "Transport & insurance of The Glass Horizon",
  approvedSuffix: "\u2022\u20221942",
  revisedSuffix: "\u2022\u20227785",
  approvedBank: "CH\u2022\u2022 \u2022\u2022\u2022\u2022 1942",
  revisedBank: "CH\u2022\u2022 \u2022\u2022\u2022\u2022 7785",
  trustedNumber: "+41 44 201 55 10 (on file)",
  revisedNumber: "+41 44 760 88 23 (new, on the PDF)",
};

const BRIEF_FACTS = [
  { label: "Your role", value: "Alex Morgan, finance coordinator" },
  { label: "Supplier", value: FIX.supplier },
  { label: "Invoice", value: `${FIX.invoiceId} \u00b7 PO ${FIX.po}` },
  { label: "Amount", value: FIX.amount },
  { label: "For", value: "The Glass Horizon, tonight's opening" },
  { label: "The request", value: "A new bank account, before 18:00" },
];

// ---------------------------------------------------------------- PC: security briefing window
const briefing: InvoiceConfig = {
  mode: "briefing",
  clock: "17:05",
  date: "Opening day",
  briefing: {
    kicker: "Bellmont Museum of Art \u00b7 Security briefing",
    title: "The Invoice That Changed",
    body: [
      "Supplier impersonation and payment redirection.",
      "Tonight's opening depends on a valuable sculpture delivery, and a trusted supplier has asked for a last-minute bank-account change.",
      "Can you stop the payment from reaching the wrong account?",
    ],
    lines: BRIEF_FACTS,
    cta: "Continue to the awareness video",
  },
  guide: {
    briefing: { title: "Security briefing", body: "Read the briefing on your screen, then click Continue to the awareness video.", actionLabel: "Continue to the awareness video" },
  },
};

// ---------------------------------------------------------------- PC: awareness film (browser window)
const film: InvoiceConfig = {
  mode: "film",
  clock: "17:06",
  date: "Opening day",
  film: {
    kicker: "Awareness video \u00b7 2 minutes",
    title: "The Invoice That Changed \u2014 When Trusted Suppliers Become a Fraud Risk",
    intro: "How attackers slip a changed bank account into a genuine supplier conversation, and the one check that stops it.",
    src: FILM,
    poster: `${ASSETS}/img/film-poster-v2.jpg`,
    watchLabel: "Watch the video",
    skipLabel: "Skip the video",
    outroLine: "Now, before you process tonight's transport invoice, take a careful look at what the supplier has sent.",
    outroVoiceId: "nova",
  },
  guide: {
    film_intro: { title: "Awareness video", body: "Click Watch the video. You can skip it if you have seen it.", actionLabel: "Watch the video" },
    film_playing: { title: "The Invoice That Changed", body: "Watch the film. The work starts when it ends." },
    film_done: { title: "Your turn", body: "Listen to the narrator, then the inbox opens." },
  },
};

// ---------------------------------------------------------------- PC: login
const login: DeskLoginConfig = {
  screenTitle: "Bellmont Museum \u2014 Operations",
  userName: "Alex Morgan",
  hint: "Your sign-in details are in the note on the right. Click it to fill them in.",
  credential: { email: ALEX_MAIL, password: "Gallery-North-24", source: "Your sign-in" },
  continueLabel: "Sign In",
};

// ---------------------------------------------------------------- PC: finance workstation (inbox + guided inspection + verify)
const finance: InvoiceConfig = {
  mode: "finance",
  clock: "17:08",
  date: "Opening day",
  finance: {
    people: { claire: CLAIRE, lena: LENA, accounts: ACCOUNTS },
    fix: FIX,
    mailClaire: {
      from: "Claire Dubois \u2014 Exhibition Director",
      time: "17:08",
      subject: "The Glass Horizon \u2014 delivery before tonight's opening",
      body: "Alex,\n\nEverything is ready for tonight's opening except our final transport confirmation. The Glass Horizon is coming from Zurich, and Aurelia's shipment is due at 17:45.\n\nPlease make sure Aurelia Fine Art Transport's invoice has been processed. Our guests arrive at 18:00, and we cannot afford a delay.\n\nThank you,\nClaire",
    },
    mailRevised: {
      from: "Lena Vogt \u2014 Aurelia Fine Art Transport AG",
      time: "17:16",
      subject: "RE: AT-4021 \u2014 Transport for Bellmont Museum",
      body: "Hi Alex,\n\nEverything is ready for today's delivery.\n\nOne important change: following our banking migration, our previous account can no longer receive transfers. Please use the updated invoice attached. The invoice number, amount, and all other details remain unchanged.\n\nOur dispatch team needs payment confirmation before releasing the shipment.\n\nMany thanks,\nLena",
      attachment: "AT-4021_REVISED.pdf",
    },
    teamsNudge: "Alex, any update? We have less than an hour before the opening.",
    replyLine: "Yes, confirmed. The old account is closed for incoming transfers. Please use the new account ending 7785 to avoid delaying the shipment. \u2014 Lena",
    revisedCall: "Yes, that's correct. We migrated to our new banking provider this week. Please use the updated account. Everything else stays exactly the same.",
    trustedCall: "A different bank account? No. We have not changed our banking details. Wait, which invoice did you receive? That is not something we sent. Please don't transfer anything until we've investigated.",
    inspectionIntro: "Go through the revised invoice item by item. For each one, decide if it is a normal detail or a warning sign. Use the Tip if you are unsure.",
    inspection: [
      {
        id: "sender",
        label: "Sender and email thread",
        value: "Lena Vogt, inside the genuine RE: AT-4021 thread, from the supplier's real address.",
        tip: "A familiar name and a real thread are not proof. A compromised mailbox still sends from the real address.",
        okFeedback: "The address and thread really are genuine, which is exactly what makes this convincing, and why it still is not proof.",
        suspiciousFeedback: "Good instinct. The address is real, but a real address from a compromised mailbox is not proof of who is writing.",
        correct: "ok",
        helpKind: "sender",
        help: { normal: "The supplier's real address, in the existing thread", warning: "A look-alike address, or a brand-new thread" },
      },
      {
        id: "invoice",
        label: "Invoice number and PO",
        value: "AT-4021, PO BM-2187. Both match the approved record.",
        tip: "Attackers resend a genuine invoice, so the number and PO usually match perfectly.",
        okFeedback: "These match the approved record. Fraudsters keep them identical on purpose, so this part is fine.",
        suspiciousFeedback: "They actually match the approved record. That is expected here and not the problem.",
        correct: "ok",
        helpKind: "amount",
        help: { normal: "Invoice number and PO that match the approved order", warning: "A number or PO that matches no order on record" },
      },
      {
        id: "amount",
        label: "Amount",
        value: "CHF 48,600.00, the same as the approved invoice.",
        tip: "The amount is usually left unchanged so the payment looks routine.",
        okFeedback: "The amount matches the approved invoice. Attackers leave it alone so nothing looks off.",
        suspiciousFeedback: "The amount matches the approved invoice, so this detail is fine.",
        correct: "ok",
        helpKind: "amount",
        help: { normal: "The same amount as the approved invoice", warning: "An amount that differs from the approved invoice" },
      },
      {
        id: "bank",
        label: "Requested bank account",
        approved: FIX.approvedBank,
        requested: FIX.revisedBank,
        isChange: true,
        tip: "A changed bank account is the whole fraud. Compare it against the account already on file.",
        okFeedback: "Look again: the account on file ends 1942, but this invoice asks for 7785. That change is the warning sign.",
        suspiciousFeedback: "Exactly. The account changed from ending 1942 to 7785 while everything else stayed the same. That is the red flag.",
        correct: "suspicious",
        helpKind: "account",
        help: { normal: "The account already on file (ending 1942)", warning: "A new account on this invoice (ending 7785)" },
      },
      {
        id: "verified",
        label: "Bank-account change verified?",
        value: "Status on record: not independently confirmed.",
        tip: "A change of payee must be confirmed through a contact you already trust, before any payment.",
        okFeedback: "It is not verified yet, so treating it as fine would be a mistake. The change has to be checked first.",
        suspiciousFeedback: "Right. An unverified change of account must be confirmed through a trusted channel before paying.",
        correct: "suspicious",
        helpKind: "verified",
        help: { normal: "Change confirmed on a channel you already trust", warning: "Change never confirmed independently" },
      },
      {
        id: "deadline",
        label: "Deadline pressure",
        value: "Payment is wanted before tonight's 18:00 opening.",
        tip: "Urgency is a pressure tactic. A real deadline never justifies skipping a payment-change control.",
        okFeedback: "The deadline is real, but it must not push you past the verification step.",
        suspiciousFeedback: "Good. The urgency is being used to rush the payment. It does not change what you must check.",
        correct: "suspicious",
        helpKind: "deadline",
        help: { normal: "A normal due date you can still verify before", warning: "Pressure to pay now, before you can check" },
      },
      {
        id: "contact",
        label: "Contact number on this PDF",
        value: "An \u201cAccounts help\u201d number printed on this invoice.",
        tip: "A number printed on the altered document can reach the attacker. Use the number already on file instead.",
        okFeedback: "Calling this number is not independent: it came from the very document you are checking.",
        suspiciousFeedback: "Correct. A number on the new invoice is not an independent channel. Use the contact already on file.",
        correct: "suspicious",
        helpKind: "number",
        help: { normal: "A number from the approved supplier record", warning: "A number printed on the new invoice itself" },
      },
    ],
    teamsReplies: [
      { id: "safe", label: "The revised bank details are unverified. I'm holding the change and checking through our approved contact.", key: "safe", eventId: "inv_notified_director" },
      { id: "pay", label: "The supplier confirmed it in their email, so I'll pay the new account.", key: "pay" },
      { id: "fraud", label: "I spoke to the supplier on our established number. They deny the change. I'm escalating suspected fraud.", key: "fraud", eventId: "inv_notified_director", needsTrusted: true },
    ],
  },
  events: {
    read_original: "inv_read_original",
    detected: "inv_detected_change",
    replied: "inv_replied_thread",
    called_revised: "inv_called_revised",
    called_trusted: "inv_called_trusted",
    reported: "inv_reported",
    notified: "inv_notified_director",
  },
  guide: {
    mail: { title: "Outlook \u2014 inbox", body: "Open Claire's message about tonight's opening. Click the highlighted message.", actionLabel: "The Glass Horizon" },
    mail_read: { title: "Claire's request", body: "Claire wants the Aurelia transport invoice processed before 18:00. Click Open the procurement record.", actionLabel: "Open the procurement record" },
    procurement: { title: "Supplier record", body: "This is the approved record for Aurelia, including the account on file. Click View original invoice.", actionLabel: "View original invoice" },
    original: { title: "Original invoice AT-4021", body: "The approved invoice: amount, PO and the account ending 1942. Click Back to the inbox.", actionLabel: "Back to the inbox" },
    revised_read: { title: "The revised invoice", body: "A new message from Lena with a changed bank account. Click Inspect the invoice.", actionLabel: "Inspect the invoice" },
    inspect: { title: "Inspect the invoice", body: "Work through each item and decide: a normal detail, or a warning sign? Tip explains what to look for.", actionLabel: "" },
    verify: { title: "Verify the bank change", body: "You found a changed account. Confirm where the money is really going before paying. Pick a way to check: press Call the contact on file, Call this number or Reply and ask.", actionLabel: "" },
    verify_denied: { title: "Supplier denies the change", body: "On the trusted number, Lena denied changing banks. The revised invoice is now DISPUTED. Press Report suspected fraud, Reply to Claire, or Go to payment review.", actionLabel: "" },
    teams: { title: "Reply to Claire", body: "Claire needs a decision. Choose your reply.", actionLabel: "" },
    call: { title: "Call", body: "Listen to the call, then click End call.", actionLabel: "End call" },
  },
};

// ---------------------------------------------------------------- PC: payment review
function paymentCfg(replay: boolean): InvoiceConfig {
  return {
    mode: "payment",
    replay,
    payment: { fix: FIX },
    events: { held: "inv_held", reported: "inv_reported", original: "inv_paid_original", redirected: "inv_paid_revised" },
    guide: {
      review: {
        title: replay ? "What would have happened" : "Payment review",
        body: replay
          ? "If the change had been accepted: the new account is selected, so click Confirm this action."
          : "Based on what you found in the inspection, choose one action, then click Confirm this action. Nothing is paid until you confirm.",
        actionLabel: "Confirm this action",
      },
      blocked: { title: "Disputed change blocked", body: "The supplier denied this change, so the payment system will not release it. Close this and choose a safe action.", actionLabel: "Close" },
      processing: { title: "Submitting", body: "The payment instruction is being processed." },
    },
  };
}

// ---------------------------------------------------------------- attacker intro (narrated card)
const attackerIntro: InvoiceConfig = {
  mode: "attackerIntro",
  attackerIntro: {
    kicker: "Attacker view",
    title: "Meet the other side",
    lines: [
      "This is a business email compromise crew. Weeks ago they quietly got into a mailbox at Aurelia, the museum's transport supplier.",
      "Since then they have been reading the real invoices and messages, learning the amounts, the people and tonight's deadline.",
      "Their goal is simple: redirect one large payment to their own account before the opening, using the supplier's genuine email thread.",
    ],
    voiceId: "onyx",
    cta: "See how they did it",
  },
  guide: {
    attackerIntro: { title: "Attacker view", body: "Who is behind the changed invoice, and what they are after.", actionLabel: "See how they did it" },
  },
};

// ---------------------------------------------------------------- PC: incident response
const incident: InvoiceConfig = {
  mode: "incident",
  incident: {
    intro: "A fraudulent payment has left the account. Minutes matter. Choose what to do first.",
    note: "A message arrives from the real supplier through the verified contact: \u201cWe have no record of receiving that payment.\u201d",
    actions: [
      { id: "bank", label: "Call the bank immediately", sub: "Report the fraud and ask for an urgent recall or hold.", priority: true, eventId: "inv_called_bank" },
      { id: "escalate", label: "Alert Finance, IT/security and management", sub: "Preserve the invoice, emails and timestamps as evidence.", eventId: "inv_escalated" },
      { id: "supplier", label: "Warn the genuine supplier via the known contact", sub: "Their mailbox may be compromised.", eventId: "inv_escalated" },
    ],
    closingNote: "Recovery is never guaranteed. Reporting fast gives the bank the best chance to stop or recall the transfer.",
    continueLabel: "Continue",
  },
  guide: {
    incident: { title: "Incident response", body: "The payment went to the attacker. Start with the bank, then escalate. Complete the priority action to continue.", actionLabel: "" },
  },
};

// ---------------------------------------------------------------- TV: summary
const summary: InvoiceConfig = {
  mode: "summary",
  summary: {
    kicker: "Bellmont Museum \u00b7 what happened",
    title: "The invoice was real. The bank details were not.",
    rows: [
      {
        q: "Did you spot the changed bank account?",
        variants: [
          { ifEvents: ["inv_detected_change"], a: "Yes \u2014 you noticed the account changed while everything else matched.", tone: "good" },
          { a: "The changed account was the only clue, and it was easy to miss.", tone: "warn" },
        ],
      },
      {
        q: "How did you verify the change?",
        variants: [
          { ifEvents: ["inv_called_trusted"], a: "You called the supplier on the number already on file. That is the check that works.", tone: "good" },
          { ifEvents: ["inv_paid_revised"], a: "The change was never verified through an independent, trusted channel.", tone: "bad" },
          { a: "You did not confirm the change through an independent, trusted channel.", tone: "warn" },
        ],
      },
      {
        q: "Was the payment instruction held, paid, or redirected?",
        variants: [
          { ifEvents: ["inv_paid_revised"], a: "Redirected: CHF 48,600 reached the attacker's account.", tone: "bad" },
          { ifEvents: ["inv_paid_original"], a: "Paid to the approved account under normal controls. No fraud.", tone: "good" },
          { a: "Held. No money moved to the wrong account.", tone: "good" },
        ],
      },
      {
        q: "Did you report it?",
        variants: [
          { ifEvents: ["inv_reported"], a: "Yes \u2014 you flagged the suspected invoice fraud so others are protected.", tone: "good" },
          { ifEvents: ["inv_called_bank"], a: "You called the bank and escalated after the payment.", tone: "good" },
          { a: "The suspicious invoice was not reported for others to see.", tone: "warn" },
        ],
      },
    ],
    closing: "Before you change where money goes, verify it through a contact you already trust.",
    continueLabel: "Continue to the debrief",
  },
};

// ---------------------------------------------------------------- hacker reconstruction
const TL_STEPS = [
  { id: "s1", title: "Supplier mailbox compromised", body: "The attacker can read Aurelia's real correspondence with the museum." },
  { id: "s2", title: "Invoice AT-4021 identified", body: "The amount and tonight's deadline are known." },
  { id: "s3", title: "Payment destination replaced", body: "Same invoice, new beneficiary ending 7785." },
  { id: "s4", title: "Altered invoice sent in the real thread", body: "From Aurelia's genuine, compromised mailbox." },
  { id: "s5", title: "Attacker-controlled number added", body: "So a call to the new PDF confirms the fraud." },
];
const hacker: MfitCloudDashConfig = {
  view: "denied",
  kicker: "Reconstruction \u00b7 how the attack worked",
  title: "Invoice fraud \u00b7 AT-4021",
  operator: "op: bec",
  voiceId: "onyx",
  narration: [
    { text: "The bank was not hacked. The museum was asked to pay a genuine invoice.", focus: "s1" },
    { text: "Inside the real supplier conversation, only one thing had been changed: the account the money goes to.", focus: "s3" },
    { text: "The new account was never confirmed through an independent, trusted contact, so the redirection was blocked.", focus: "s6" },
  ],
  steps: [
    ...TL_STEPS.map((s) => ({ ...s, state: "done" as const })),
    {
      id: "s6",
      title: "Final outcome",
      body: "The bank change was verified through the trusted contact and rejected.",
      state: "denied" as const,
      statusLabel: "Blocked",
      stateIf: [
        { events: ["inv_paid_revised"], state: "failed" as const, statusLabel: "Redirected", body: "CHF 48,600 was sent to the attacker's account." },
        { replay: true, state: "failed" as const, statusLabel: "Would be redirected", body: "CHF 48,600 would have been sent to the attacker's account." },
      ],
    },
  ],
  deniedNote: "ATTACK BLOCKED \u00b7 The bank change was checked through the trusted contact and rejected. No transfer was made.",
  variants: [
    {
      ifReplay: true,
      config: {
        kicker: "Reconstruction \u00b7 what would have happened",
        narration: [
          { text: "If the change had been accepted, the altered invoice would have run through the normal payment workflow.", focus: "s4" },
          { text: "The money would have left for the new account inside the genuine conversation.", focus: "s3" },
          { text: "CHF 48,600 would have been credited to the attacker, not to Aurelia.", focus: "s6" },
        ],
        deniedNote: "WHAT WOULD HAVE HAPPENED \u00b7 CHF 48,600 would have been sent to the attacker's account.",
      },
    },
    {
      ifEvents: ["inv_paid_revised"],
      config: {
        narration: [
          { text: "The bank was not hacked. The museum followed a payment instruction that had been altered inside a trusted supplier conversation.", focus: "s4" },
          { text: "Only the beneficiary account was changed. Everything else matched the real invoice.", focus: "s3" },
          { text: "CHF 48,600 was credited to the attacker's account. The supplier was never paid.", focus: "s6" },
        ],
        deniedNote: "PAYMENT REDIRECTED \u00b7 CHF 48,600 was sent to the attacker's account. The supplier was never paid.",
      },
    },
  ],
  routes: [
    { key: "redirected", ifEvents: ["inv_paid_revised"] },
    { key: "replay", ifReplay: true },
    { key: "blocked" },
  ],
  continueLabel: "Continue",
};

// ---------------------------------------------------------------- real-life cases (debrief newspaper)
const REAL_CASES = {
  buttonLabel: "See real-life fraud cases",
  masthead: "THE LEDGER \u00b7 FRAUD FILES",
  cases: [
    {
      date: "2019 \u00b7 United States",
      headline: "One fake supplier drained USD 120 million from Google and Facebook",
      standfirst: "A single impersonated hardware vendor, years of fake invoices.",
      image: `${ASSETS}/img/case-a.jpg`,
      sections: [
        { h: "What happened", body: "Between 2013 and 2015, two of the world's largest technology companies wired more than USD 120 million to bank accounts controlled by a Lithuanian man, Evaldas Rimasauskas. Facebook sent about USD 99 million and Google about USD 23 million." },
        { h: "How the deception worked", body: "He registered a company with the same name as Quanta Computer, a real hardware supplier both firms used, then sent fake invoices and look-alike emails telling staff to pay the supplier's \u201cnew\u201d accounts in Latvia and Cyprus." },
        { h: "What you can learn", body: "A real supplier name is not proof. A new or changed payee must be checked against the vendor record and a contact you already trust. Google recovered its funds and Facebook the bulk of its; Rimasauskas was sentenced to five years in 2019." },
      ],
    },
    {
      date: "2016 \u00b7 Belgium",
      headline: "Belgian bank Crelan loses about EUR 70 million to \u201cfake president\u201d fraud",
      standfirst: "Instructions that seemed to come from the CEO.",
      image: `${ASSETS}/img/case-b.jpg`,
      sections: [
        { h: "What happened", body: "In January 2016, Crelan announced it had lost up to EUR 70 million in an international fraud, discovered by its own internal controls. No customers were affected; the bank absorbed the loss from reserves and informed prosecutors." },
        { h: "How the deception worked", body: "Fraudsters impersonated the bank's CEO and, under that apparent authority, instructed staff to carry out transfers under false pretences, a scheme known as \u201cfake president\u201d or CEO fraud." },
        { h: "What you can learn", body: "Seniority is not a reason to skip a control. An instruction that appears to come from the top still has to pass the normal payment and change-of-payee checks." },
      ],
    },
    {
      date: "2019 \u00b7 United States / Hong Kong",
      headline: "Nikkei's US arm sends about USD 29 million on a fake executive's orders",
      standfirst: "An urgent instruction from \u201cmanagement\u201d.",
      image: `${ASSETS}/img/case-c.jpg`,
      sections: [
        { h: "What happened", body: "In late September 2019, an employee at Nikkei America transferred about USD 29 million to a Hong Kong account after receiving fraudulent instructions from someone posing as an executive of its parent company, Nikkei. The company reported it to US and Hong Kong authorities and worked to recover the funds." },
        { h: "How the deception worked", body: "The attacker impersonated a senior executive and used that apparent authority to request an urgent transfer with new payment details." },
        { h: "What you can learn", body: "Urgent payment instructions from \u201cmanagement\u201d must be confirmed through a channel you already trust, never through the same message or number that made the request." },
      ],
    },
    {
      date: "2024 \u00b7 Hong Kong",
      headline: "Deepfake video call tricks a finance worker into paying about USD 25 million",
      standfirst: "Everyone on the call was fake.",
      image: `${ASSETS}/img/case-d.jpg`,
      sections: [
        { h: "What happened", body: "In January 2024, a finance employee at the Hong Kong office of engineering firm Arup made 15 transfers totalling about HKD 200 million (around USD 25.6 million) to five bank accounts. Arup confirmed the fraud; Hong Kong police called it one of the largest deepfake scams they had seen." },
        { h: "How the deception worked", body: "The employee received a message that appeared to be from the UK-based CFO about a confidential transaction, then joined a video call where the CFO and colleagues were all AI deepfakes. Convinced the people were real, they released the payments." },
        { h: "What you can learn", body: "Even a face and a voice on a video call can be faked. A payment, or a bank change, must still be verified through a separate, trusted channel before the money moves." },
      ],
    },
  ],
};

// ---------------------------------------------------------------- scenario
export const invoiceThatChangedScenario: ScenarioSpec = {
  version: 1,
  id: "invoice-that-changed",
  title: "The Invoice That Changed: Supplier Payment Fraud",
  topic: "Supplier impersonation, business email compromise and payment redirection",
  language: "en",
  summary: {
    goal: "Stop a changed supplier bank account from redirecting a genuine payment, and verify it the way that actually works.",
    youDo: "Inspect a trusted supplier's invoice item by item, spot a changed bank account inside a real email thread, verify it through the right channel under a deadline, and decide on the payment. See the attack from the other side.",
    duration: "12-15 min, including a ~2-minute film",
    tags: ["payment_fraud", "verification", "email_phishing", "incident_reporting"],
    audience: ["finance", "all_staff"],
    needs: ["sound"],
  },
  ambientUrl: "/cdn/sim/invoice-that-changed/audio/museum-classical.mp3",
  ambientVolume: 0.22,
  music: "none",
  passThreshold: 60,
  riskMeter: false,
  exposureMap: true,
  allowFreeExplore: false,

  scene: {
    environment: MUSEUM_ENV,
    environments: [
      { id: "museum", environment: MUSEUM_ENV, ambientUrl: "/cdn/sim/invoice-that-changed/audio/museum-classical.mp3", ambientVolume: 0.22, music: "none" },
      { id: "hacker", environment: HACKER_ENV, ambientUrl: "", music: "dark" },
    ],
    props: [],
    waypoints: [
      // establishing: stand near the crossing, look down the south wing toward the desk (the tablet is off to the west and never framed)
      { id: "wp_inv_wide", position: [1.8, 2.35, 6.2], target: [0, 1.25, -15], fov: 60, durationS: 0 },
      // walk down the south wing toward the desk
      { id: "wp_inv_walk", position: [0.6, 1.72, -5.5], target: [0, 1.1, -15.4], fov: 52, durationS: 4.2, ease: "easeInOut" },
      // dead-on the desk monitor glass (0.48 x 0.28 m) at 0.62 m, fov 40 -> DOM fills 62% of viewport height
      { id: "wp_inv_pc", position: [0, 0.96, -14.798], target: [0, 0.96, -15.418], fov: 40, durationS: 3.0, ease: "easeInOut" },
      { id: "wp_inv_pc_cut", position: [0, 0.96, -14.798], target: [0, 0.96, -15.418], fov: 40, durationS: 0 },
      // north-wall TV for the summary
      { id: "wp_inv_tv2", position: [0, 2.2, 12.379], target: [0, 2.2, 15.518], fov: 45, durationS: 3.2, ease: "easeInOut" },
      waypoint(quishingRestaurantScenario, "wp_hacker_establish", "wp_inv_hacker"),
      waypoint(quishingRestaurantScenario, "wp_hacker_laptop", "wp_inv_hacker_laptop"),
    ],
    initialWaypointId: "wp_inv_wide",
  },

  devices: [
    { id: "inv_briefing", kind: "invoice", config: briefing },
    { id: "inv_film", kind: "invoice", config: film },
    { id: "inv_login", kind: "desklogin", config: login },
    { id: "inv_finance", kind: "invoice", config: finance },
    { id: "inv_payment", kind: "invoice", config: paymentCfg(false) },
    { id: "inv_payment_replay", kind: "invoice", config: paymentCfg(true) },
    { id: "inv_attacker_intro", kind: "invoice", config: attackerIntro },
    { id: "inv_hacker", kind: "mfitclouddash", config: hacker },
    { id: "inv_incident", kind: "invoice", config: incident },
    { id: "inv_summary", kind: "invoice", config: summary },
  ],

  voiceAttackers: {
    invoice: {
      persona: { name: "Attacker", role: "Business email compromise operator", pretext: "it_support", voiceId: "onyx" },
      goal: "Redirect a genuine supplier payment to a changed bank account",
      opening: "",
      allowedTopics: [],
      scoringEvents: EVENTS,
    },
  },

  initialBeatId: "inv_intro",
  beats: [
    {
      id: "inv_intro",
      type: "intro",
      title: "The Invoice\nThat Changed",
      kicker: "Supplier payment fraud \u00b7 business email compromise",
      highlight: "last-minute bank-account change",
      icon: "folder",
      ctaLabel: "Start",
      guide: {
        title: "Opening day at Bellmont Museum",
        body: "You are Alex Morgan, operations and finance coordinator. A valuable artwork is still in transit, and a trusted supplier has asked for a last-minute bank-account change.",
      },
    },
    { id: "inv_cam_walk", type: "camera", waypointId: "wp_inv_walk", walkSfx: true, guide: { title: "The gallery", body: "Walking to the finance workstation in the south wing." } },
    { id: "inv_cam_pc", type: "camera", waypointId: "wp_inv_pc", walkSfx: true, guide: { title: "The finance workstation", body: "The desk computer at the end of the south wing." } },
    { id: "inv_login_beat", type: "device", deviceId: "inv_login", guide: { title: "Sign in", body: "Click the login screen, fill in your details from the note, then click Sign In.", actionLabel: "Sign In" }, completeOn: { action: "continue" } },
    { id: "inv_briefing_beat", type: "device", deviceId: "inv_briefing", screen: "on", guide: { title: "Security briefing", body: "Read the briefing." }, completeOn: { action: "continue" } },
    { id: "inv_film_beat", type: "device", deviceId: "inv_film", screen: "on", guide: { title: "Awareness video", body: "Watch the video, or skip it." }, completeOn: { action: "continue" } },
    { id: "inv_finance_beat", type: "device", deviceId: "inv_finance", screen: "on", preloadEnvironmentId: "hacker", guide: { title: "Finance workstation", body: "Work through the invoice." }, completeOn: { action: "continue" } },
    { id: "inv_payment_beat", type: "device", deviceId: "inv_payment", screen: "on", guide: { title: "Payment review", body: "Decide on the payment." }, completeOn: { action: "continue" } },

    // reconstruction (actual outcome): cut to the attacker room, introduce the crew, then the timeline
    { id: "inv_cut_hacker", type: "camera", waypointId: "wp_inv_hacker", environmentId: "hacker", fadeCut: true, alertSfx: true, guide: { title: "Behind the scenes", body: "Who changed the invoice?" } },
    { id: "inv_attacker_intro_beat", type: "device", deviceId: "inv_attacker_intro", guide: { title: "Attacker view", body: "Meet the other side." }, completeOn: { action: "continue" } },
    { id: "inv_push_hacker", type: "camera", waypointId: "wp_inv_hacker_laptop", guide: { title: "The reconstruction", body: "Follow the invoice." } },
    { id: "inv_hacker_beat", type: "device", deviceId: "inv_hacker", guide: { title: "Reconstruction", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },

    // unsafe branch: teach + incident response
    {
      id: "inv_teach_risky",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "The invoice was genuine, the supplier was genuine, and the mailbox was genuine but compromised. Only the bank account had been changed. Because the change was not confirmed through an independent, trusted channel, CHF 48,600 went to the attacker.",
        spot: ["A bank-account change inside an otherwise normal invoice", "Urgency and a real deadline pushing you to pay now", "\u201cConfirmation\u201d that comes from the same email thread or a number on the new PDF"],
        protect: ["Verify any change of payee on a number already on file, from before the request", "A matching invoice number, amount and sender are not verification", "Hold a disputed change and report it through your payment-change control"],
        remember: "Before money moves to a new account, verify it through a contact you already trust.",
        continueLabel: "What to do now",
      },
    },
    { id: "inv_cam_incident", type: "camera", waypointId: "wp_inv_pc_cut", environmentId: "museum", fadeCut: true, guide: { title: "Back at the workstation", body: "Respond to the fraudulent payment." } },
    { id: "inv_incident_beat", type: "device", deviceId: "inv_incident", screen: "on", guide: { title: "Incident response", body: "Act on the payment." }, completeOn: { action: "continue" } },

    // safe branch: teach + what-would-have-happened replay
    {
      id: "inv_teach_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        whatIf: "If Alex had paid the changed account without verifying it",
        body: "You stopped a genuine-looking payment from reaching the wrong account. The invoice, amount and sender all matched; only the bank details had been altered inside a real supplier conversation. Here is what one unverified payment would have cost.",
        spot: ["A bank-account change inside an otherwise normal invoice", "Urgency and a real deadline pushing you to pay now", "\u201cConfirmation\u201d that comes from the same email thread or a number on the new PDF"],
        protect: ["Verify any change of payee on a number already on file, from before the request", "A matching invoice number, amount and sender are not verification", "Hold and report a disputed change through your payment-change control"],
        remember: "Before money moves to a new account, verify it through a contact you already trust.",
        continueLabel: "See what would have happened",
      },
    },
    { id: "inv_cam_pc_replay", type: "camera", waypointId: "wp_inv_pc_cut", environmentId: "museum", fadeCut: true, whatIfNote: "If Alex had submitted the changed account without verifying it.", guide: { title: "What would have happened", body: "The same payment, with the changed account." } },
    { id: "inv_payment_replay_beat", type: "device", deviceId: "inv_payment_replay", screen: "on", forced: true, whatIfNote: "If Alex had submitted the changed account without verifying it.", guide: { title: "What would have happened", body: "Submit the revised account instruction." }, completeOn: { action: "continue" } },
    { id: "inv_cut_hacker_r", type: "camera", waypointId: "wp_inv_hacker", environmentId: "hacker", fadeCut: true, alertSfx: true, whatIfNote: "If Alex had submitted the changed account without verifying it.", guide: { title: "What would have happened", body: "The same reconstruction, with the payment made." } },
    { id: "inv_push_hacker_r", type: "camera", waypointId: "wp_inv_hacker_laptop", whatIfNote: "If Alex had submitted the changed account without verifying it.", guide: { title: "What would have happened", body: "Follow the invoice." } },
    { id: "inv_hacker_r_beat", type: "device", deviceId: "inv_hacker", whatIfNote: "If Alex had submitted the changed account without verifying it.", guide: { title: "What would have happened", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },

    // results
    { id: "inv_cam_tv_results", type: "camera", waypointId: "wp_inv_tv2", environmentId: "museum", fadeCut: true, endsWhatIf: true, guide: { title: "The gallery", body: "The result goes up on the wall screen." } },
    { id: "inv_summary_beat", type: "device", deviceId: "inv_summary", guide: { title: "What happened", body: "Read the summary, then click Continue to the debrief.", actionLabel: "Continue to the debrief" }, completeOn: { action: "continue" } },
    {
      id: "inv_debrief_terminal",
      type: "terminal_debrief",
      terminalDebrief: {
        intro: "Here is what happened, and the four things to remember.",
        continueLabel: "Continue to the quiz",
        realCases: REAL_CASES,
        topics: [
          {
            id: "inv_t_change",
            dimension: "money",
            title: "The change is the risk",
            lines: [
              "A new bank account is a high-risk request even when the invoice number, amount, logo and sender all look right.",
              "Attackers resend a genuine invoice with only the beneficiary changed.",
            ],
          },
          {
            id: "inv_t_verify",
            dimension: "operations",
            title: "Verify on a channel you already trust",
            lines: [
              "Confirm a changed payee on a number from the approved record or contract, from before the request.",
              "A reply in the same thread or a number on the new invoice reaches whoever made the change.",
            ],
          },
          {
            id: "inv_t_controls",
            dimension: "operations",
            title: "Hold, don't rush",
            lines: [
              "A real deadline does not justify bypassing a payment-change control.",
              "Hold a disputed change; a disputed account cannot be released until it is verified.",
            ],
          },
          {
            id: "inv_t_respond",
            dimension: "money",
            title: "If it already went, act fast",
            lines: [
              "Call the bank first to try to recall or hold the transfer, then alert Finance, IT and the supplier.",
              "The supplier's mailbox may be compromised, and recovery is never guaranteed.",
            ],
          },
        ],
      },
    },
    {
      id: "inv_quiz",
      type: "quiz",
      title: "Check what you learned",
      quiz: [
        {
          id: "q_signal",
          title: "The one change",
          prompt: "A supplier emails a revised invoice. The invoice number, amount, PO and sender all match the original. Only the bank account is different. What should you treat this as?",
          options: [
            { id: "a", label: "Routine: everything else matches, so pay the new account" },
            { id: "b", label: "A high-risk change of payee that must be verified before paying", correct: true },
            { id: "c", label: "Safe, because it came from the supplier's real email address" },
          ],
          rationale: "A changed bank account is the whole fraud. Matching details and a real sender do not make it safe.",
        },
        {
          id: "q_verify",
          title: "How to verify",
          prompt: "How do you confirm the new bank details actually come from the supplier?",
          options: [
            { id: "a", label: "Reply to the email and ask them to confirm" },
            { id: "b", label: "Call the number printed on the revised invoice" },
            { id: "c", label: "Call the supplier on a number from the approved record, from before the request", correct: true },
          ],
          rationale: "Only an independent channel you already had reaches the real supplier. The thread and the new number reach the attacker.",
        },
        {
          id: "q_reply",
          title: "The email reply",
          prompt: "You reply in the thread and get \u201cYes, confirmed, use the new account.\u201d Does that verify the change?",
          options: [
            { id: "a", label: "No: the mailbox is compromised, so the reply is from the attacker", correct: true },
            { id: "b", label: "Yes: it came from the supplier's address" },
            { id: "c", label: "Yes, as long as the invoice number matches" },
          ],
          rationale: "A reply to a compromised mailbox is answered by whoever controls it. Same address, same attacker.",
        },
        {
          id: "q_pressure",
          title: "The deadline",
          prompt: "The director needs the shipment released before the 18:00 opening and is pressing for a decision. What is the right call?",
          options: [
            { id: "a", label: "Pay the new account now; the deadline matters most" },
            { id: "b", label: "Hold the unverified change and verify it, even if the shipment is delayed", correct: true },
            { id: "c", label: "Pay now and verify afterwards" },
          ],
          rationale: "A real deadline never justifies bypassing a payment-change control. A delay is an operational issue, not a security failure.",
        },
        {
          id: "q_disputed",
          title: "A disputed change",
          prompt: "You reached the supplier on the trusted number and they deny changing banks. What happens to the revised account?",
          options: [
            { id: "a", label: "Pay it anyway to keep the shipment on time" },
            { id: "b", label: "It is disputed and held, and the suspected fraud is reported", correct: true },
            { id: "c", label: "Ask the supplier to email confirmation instead" },
          ],
          rationale: "A disputed change is held and reported. It cannot be released until it is genuinely verified.",
        },
        {
          id: "q_after",
          title: "After a payment goes",
          prompt: "A fraudulent payment has already been sent. What do you do first?",
          options: [
            { id: "a", label: "Wait to see if the supplier confirms receipt" },
            { id: "b", label: "Call the bank immediately to try to recall or hold the transfer, then escalate", correct: true },
            { id: "c", label: "Email the attacker's thread to ask for the money back" },
          ],
          rationale: "Speed matters. The bank may stop or recall the transfer, then Finance, IT and the supplier must be alerted. Recovery is not guaranteed.",
        },
      ],
    },
    { id: "inv_debrief", type: "debrief", title: "Debrief" },
  ],

  edges: [
    { from: "inv_intro", to: "inv_cam_walk" },
    { from: "inv_cam_walk", to: "inv_cam_pc" },
    { from: "inv_cam_pc", to: "inv_login_beat" },
    { from: "inv_login_beat", to: "inv_briefing_beat" },
    { from: "inv_briefing_beat", to: "inv_film_beat" },
    { from: "inv_film_beat", to: "inv_finance_beat" },
    { from: "inv_finance_beat", to: "inv_payment_beat" },
    { from: "inv_payment_beat", to: "inv_cut_hacker" },
    { from: "inv_cut_hacker", to: "inv_attacker_intro_beat" },
    { from: "inv_attacker_intro_beat", to: "inv_push_hacker" },
    { from: "inv_push_hacker", to: "inv_hacker_beat" },
    { from: "inv_hacker_beat", to: "inv_teach_risky", on: "redirected" },
    { from: "inv_hacker_beat", to: "inv_teach_safe", on: "blocked" },
    { from: "inv_hacker_beat", to: "inv_teach_safe" },
    { from: "inv_teach_risky", to: "inv_cam_incident" },
    { from: "inv_cam_incident", to: "inv_incident_beat" },
    { from: "inv_incident_beat", to: "inv_cam_tv_results" },
    { from: "inv_teach_safe", to: "inv_cam_pc_replay" },
    { from: "inv_cam_pc_replay", to: "inv_payment_replay_beat" },
    { from: "inv_payment_replay_beat", to: "inv_cut_hacker_r" },
    { from: "inv_cut_hacker_r", to: "inv_push_hacker_r" },
    { from: "inv_push_hacker_r", to: "inv_hacker_r_beat" },
    { from: "inv_hacker_r_beat", to: "inv_cam_tv_results", on: "replay" },
    { from: "inv_hacker_r_beat", to: "inv_cam_tv_results" },
    { from: "inv_cam_tv_results", to: "inv_summary_beat" },
    { from: "inv_summary_beat", to: "inv_debrief_terminal" },
    { from: "inv_debrief_terminal", to: "inv_quiz" },
    { from: "inv_quiz", to: "inv_debrief" },
  ],
};
